import { Box, Grid, LinearProgress, Table, TableBody, TableCell, TableRow } from '@material-ui/core';
import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, ConfigProvider, Typography } from 'antd';
import classnames from 'classnames';
import { DateTime } from 'luxon';
import React, { CSSProperties, useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { ErrorBoundary } from 'react-error-boundary';
import { PieChart } from 'react-minimal-pie-chart';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import RadarChart from 'react-svg-radar-chart';
import 'react-svg-radar-chart/build/css/index.css';
import { metricsApi, patientApi, patientReportApi } from '../api';
import { AveragePlateMealDataResponse, PatientSummaryResponse } from '../api/generated';
import { CircleDot } from '../components/circleDot';
import { LabelAA, LabelM } from '../components/label';
import { LoadingSpinner } from '../components/loadingSpinner';
import { RxErrorBoundary } from '../components/RxErrorBoundary';
import { useCurrentPatientData, useStore } from '../context';
import { TFunc, Trans, useTranslation } from '../i18n';
import { PatientService } from '../services/patient';
import { sanitizeNoteHtml } from '../utils/sanitizeNoteHtml';
import './patient-summary.scss';
import { makeStyles } from '@material-ui/core/styles';
import { ANALYTICS_EVENTS, track } from '../analytics';
import { AgpDateRange } from '../components/AGPData';
import { useDateRange, withFilterPanel } from '../components/dateRangeFilter';

const iconBoxStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const NeedToKnow: React.FunctionComponent = (props) => {
  const { patientSummary, patient, hei } = useStore();
  const { nutrients = {} } = patientSummary;
  const { frequency = {} } = patientSummary;
  const patientService = PatientService();
  const { t } = useTranslation();
  function statement(name, status) {
    if (!status) {
      return '';
    }
    const { analysis } = status;

    let levelStr, classes;
    /* eslint-disable i18next/no-literal-string */
    if (analysis == 'high') {
      levelStr = 'high';
      classes = 'warning';
    } else if (analysis == 'low') {
      levelStr = 'low';
      classes = 'warning';
    } else {
      levelStr = 'at target';
      classes = 'normal';
    }
    /* eslint-enable i18next/no-literal-string */
    return (
      <div className="needToKnow">
        <span className={classes}>{levelStr}</span> <Trans>in</Trans> {name}
      </div>
    );
  }

  return (
    <div>
      <div className="summaryTitle">
        <Trans>What you need to know</Trans>
      </div>
      <div>
        {statement(t('carbohydrates'), nutrients.carbs)}
        {statement(t('fiber'), nutrients.fiber)}
        {statement(t('calories'), nutrients.calories)}
        {statement(t('protein'), nutrients.protein)}
        {statement(t('Processed Food'), frequency.processed)}
      </div>
    </div>
  );
};

const HealthyEatingPlot: React.FunctionComponent = (props) => {
  const { patientSummary, patient, hei } = useStore();
  const { nutrients = {} } = patientSummary;
  const { frequency = {} } = patientSummary;
  const patientService = PatientService();
  const { t } = useTranslation();
  useEffect(() => {
    const patient_id = patient.patient_id;
    const getDataHEI = async () => {
      const heiData = await patientService.getHEI(patient_id);
      if (heiData && document.getElementById('tooltip')) {
        console.log(heiData);
        document.getElementById('tooltip').innerText = (heiData.hei_score)
          ? t('Total Score') + ': ' + parseInt(heiData.hei_score).toString() + '. '
            + t('Hover over dots for more information')
          : '';
      }
    };
    getDataHEI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const labelTips = {
    sat_fat: t('less sat fats (<8% of energy) '),
    vegetables: t('more vegetables and legumes '),
    fish_plant_protein: t('more seafood, soy, nuts, and legumes '),
    whole_Grain: t('more whole grains (>1.5oz eq per 1000kcal) '),
    refined_Grain: t('less refrained grains (<1.8oz eq per 1000kcal) '),
    sodium: t('less sodium (<1.1g per 1000kcal) '),
    dairy: t('more diary (>1.3 cup eq per 1000kcal) '),
    fruits: t('more fruit'),
    fatty_acids: t('more MUFA and PUFA relative to Sat fats '),
  };

  const data2 = [
    {
      data: {
        sat_fat: (hei) ? hei.saturated_fats_g / 10 : 0,
        vegetables: (hei) ? (hei.total_vegetables_cups / 5) : 0,
        fish_plant_protein: (hei) ? hei.seafood_plant_proteins_oz / 5 : 0,
        whole_Grain: (hei) ? hei.whole_grains_oz / 10 : 0,
        refined_Grain: (hei) ? hei.refined_grains_oz / 10 : 0,
        sodium: (hei) ? hei.sodium_mg / 10 : 0,
        dairy: (hei) ? hei.dairy_cups / 10 : 0,
        fruits: (hei) ? hei.whole_fruits_cups / 5 : 0,
        fatty_acids: (hei) ? hei.fatty_acids_g / 10 : 0,
      },
      meta: { color: 'blue' },
    },
    /*      {
        data: {
          sat_fat: 0,
          vegetables: 0,
          fish_plant_protein: 0,
          whole_Grain: 0,
          refined_Grain: 0,
          sodium: 0,
          dairy: 0,
          fruits: 0,
          fatty_acids: 0
        },
        meta: { color: 'red' }
      }
      */
  ];

  const captions = {
    // columns
    refined_Grain: t('Refined Grains'),
    vegetables: t('Vegetables'),
    dairy: t('Dairy'),
    fish_plant_protein: t('Fish/Plant'),
    whole_Grain: t('Whole Grains'),
    sat_fat: t('Sat Fat'),
    sodium: t('Sodium'),
    fruits: t('Fruits'),
    fatty_acids: t('Fatty Acids'),
  };

  const defaults = {
    axes: false,
    dots: true,
    captionProps: () => ({
      className: 'caption',
      textAnchor: 'middle',
      fontSize: 11,
    }),
    dotProps: () => ({
      className: 'dot',
      mouseEnter: (dot) => {
        let mscale = 10;
        const labelKey = dot.key;
        if (dot.key == 'fish_plant_protein' || dot.key == 'fruits') {
          mscale = 5;
        }
        const tscore = (dot.value * mscale).toFixed(1);
        document.getElementById('tooltip').innerText = dot.key + ': ' + tscore.toString() + ' (' + t('max score') + ' '
          + mscale.toString() + ')' + ' ' + t('eat') + ' ' + labelTips[labelKey] + ' ' + t('for max score.');
        document.getElementById('tooltip').style.visibility = 'visible';
      },
      mouseLeave: (dot) => {
        document.getElementById('tooltip').innerText = (hei)
          ? t('Total Score{{colon}} {{score}}. Hover over dots for more information', {
            colon: ':',
            score: parseInt(hei.hei_score).toString(),
          })
          : t('Loading...');
      },
    }),
  };

  const radarChart = (
    <div>
      <div className="summaryHEI" id="divtool">
        <label id="tooltip">
          <Trans>Loading...</Trans>
        </label>
      </div>
      <RadarChart captions={captions} data={data2} size={310} options={defaults} />
      <div className="summaryText">
        <Trans>
          Based on last 100 days. Disclaimer: NIH HEI may not be applicable if user doesn't eat certain foods.
        </Trans>{' '}
        <a href="https://epi.grants.cancer.gov/hei/hei-2015-table1.html" target="_blank">
          <u>
            <Trans>Learn More</Trans>
          </u>
        </a>
      </div>
    </div>
  );

  return (
    <div>
      <div className="summaryTitle">
        <Trans>Healthy Eating Index</Trans>
      </div>
      <div className="summaryText">{radarChart}</div>
    </div>
  );
};

const getProcessedFrequencies = (patientSummary: PatientSummaryResponse, t: TFunc) => {
  return {
    processed: [
      {
        name: t('ultra & high processed'),
        value: (patientSummary.frequency_high_processed * 100).toFixed(0),
        analysis: patientSummary.frequency_high_processed > 0.5 ? 'high' : 'at target',
      },
      {
        name: t('min processed'),
        value: (patientSummary.frequency_min_processed * 100).toFixed(0),
        analysis: 'low',
      },
    ],
    meat: [
      {
        name: t('red meat'),
        value: (patientSummary.frequency_redmeat * 100).toFixed(0),
        analysis: patientSummary.frequency_redmeat > 0.2 ? 'high' : 'at target',
      },
      {
        name: t('poultry'),
        value: (patientSummary.frequency_poultry * 100).toFixed(0),
        analysis: 'low',
      },
      {
        name: t('fish'),
        value: (patientSummary.frequency_fish * 100).toFixed(0),
        analysis: 'low',
      },
      {
        name: t('plant'),
        value: (patientSummary.frequency_plant * 100).toFixed(0),
        analysis: 'low',
      },
    ],
    fiber: [
      {
        name: t('high fiber'),
        value: (patientSummary.frequency_high_fiber * 100).toFixed(0),
        analysis: patientSummary.frequency_high_fiber < 0.6 ? 'high' : 'at target',
      },
      {
        name: t('low fiber'),
        value: (patientSummary.frequency_low_fiber * 100).toFixed(0),
        analysis: 'low',
      },
    ],
    fried: [
      {
        name: t('fried'),
        value: (patientSummary.frequency_fried * 100).toFixed(0),
        analysis: patientSummary.frequency_fried > 0.2 ? 'high' : 'at target',
      },
      {
        name: t('not fried'),
        value: (patientSummary.frequency_not_fried * 100).toFixed(0),
        analysis: 'low',
      },
    ],
  };
};

const Frequency: React.FunctionComponent = (props) => {
  const { patient } = useStore();
  const { t } = useTranslation();

  const patientSummaryQuery = useQuery(['patient-summary', patient.patient_id], async () => {
    const res = await patientReportApi.appApiPatientSummaryPatientSummary({
      patient_id: patient.patient_id,
      date: DateTime.local().toISODate(),
    });
    return res.data;
  });
  const processedFrequencies = patientSummaryQuery.data && getProcessedFrequencies(patientSummaryQuery.data, t);

  function distributionGraph(data) {
    if (!data) {
      return '';
    }

    return (
      <Row noGutters={true}>
        {data.map(item => {
          const growValue = item.value === '0' ? 1 : item.value;
          const styles = { flexGrow: growValue };
          return (
            <Col key={item.name} style={styles} className={classnames('freqSection', item.analysis)}>
              <LabelAA className="freqLabel">{item.name}</LabelAA>
              <LabelM className="freqValue">{item.value}%</LabelM>
            </Col>
          );
        })}
      </Row>
    );
  }

  return (
    patientSummaryQuery.isLoading ? <LoadingSpinner /> : !!patientSummaryQuery.data && (
      <div>
        <div className="summaryTitle">
          <Trans>Frequency Data (avg week)</Trans>
        </div>
        <div>
          {distributionGraph(processedFrequencies.processed)}
          {distributionGraph(processedFrequencies.meat)}
          {distributionGraph(processedFrequencies.fiber)}
          {distributionGraph(processedFrequencies.fried)}
        </div>
      </div>
    )
  );
};

interface NutrientProp {
  name: string;
  value: any;
  className?: string;
}

const Nutrient: React.FunctionComponent<NutrientProp> = (props) => {
  const { name, className, value = {} } = props;
  const { value: quantity, analysis } = value;

  const classes = classnames('panelCol', 'nutrientCol', className, analysis);

  return (
    <Col className={classes}>
      <div className="value">{Number(quantity).toFixed(0)}</div>
      <div className="name">{name}</div>
    </Col>
  );
};

const EmotionData: React.FunctionComponent = (props) => {
  const { emotion, emotionQuestion, patient } = useStore();
  const patientService = PatientService();

  useEffect(() => {
    const patient_id = patient.patient_id;
    const getDataEmotion = async () => {
      await patientService.getEmotion(patient_id);
    };
    getDataEmotion();

    const getDataEmotionQuestion = async () => {
      await patientService.getEmotionQuestion(patient_id);
    };
    getDataEmotionQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function outputStatementQuestion(status) {
    if (!status) {
      return '';
    }
    return (
      <div className="summaryTitle">
        <Trans>User feedback</Trans>: {status.question}
      </div>
    );
  }

  function outputStatement(status) {
    if (!status) {
      return '';
    }
    return (
      <div>
        {status.map((item, idx) => {
          console.log(item.emotion);
          return <p key={idx}>{item.log_date}: {item.emotion} {item.note}</p>;
        })}
      </div>
    );
  }

  return (
    <div>
      {console.log(emotion)}
      {console.log(emotionQuestion)}
      <div>
        {outputStatementQuestion(emotionQuestion)}
        {outputStatement(emotion)}
      </div>
    </div>
  );
};

const useMedScoreProgressStyles = makeStyles({
  progressBar: (props: { color: string }) => ({
    width: '100%',
    height: 25,
    borderRadius: 5,
    backgroundColor: `${props.color}20`,
    '& .MuiLinearProgress-bar': {
      backgroundColor: props.color,
    },
  }),
});

const MedScoreLinearProgress = ({ value, color = '#36c2b4' }) => {
  const classes = useMedScoreProgressStyles({ color });

  return (
    <Box position="relative" display="flex" alignItems="center">
      <LinearProgress
        variant="determinate"
        value={value * 100}
        className={classes.progressBar}
      />
      <Box
        position="absolute"
        right={0}
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        height="100%"
        paddingRight="8px"
      >
        <Typography>
          {Math.round(value * 10) / 10}
        </Typography>
      </Box>
    </Box>
  );
};

const Summary = (props: { since: moment.Moment, until: moment.Moment, hasUpdatedDates: boolean }) => {
  const { patient } = useStore();
  const { flags } = useCurrentPatientData();
  const { t } = useTranslation();
  const { since, until, hasUpdatedDates } = props;

  const averagePlateQuery = useQuery(
    ['patient-average-plate', patient.patient_id, since, until],
    async () => {
      const res = await patientReportApi.appApiPatientReportsSectionsAveragePlateGetAveragePlate({
        patient_id: patient.patient_id,
        date_since: since.format('YYYY-MM-DD'),
        date_until: until.format('YYYY-MM-DD'),
      });
      return res.data;
    },
    { enabled: hasUpdatedDates },
  );

  const averagePlateData = averagePlateQuery.data;

  const breakfastIcon = () => {
    return (
      <svg viewBox="0 0 30 30" width="30" height="30" fill="#000000">
        <path d="M 6.071 2.203 C 6.071 1.025 6.619 0.267 7.689 0 L 7.832 0.506 C 7.019 0.709 6.627 1.251 6.627 2.203 C 6.627 2.749 6.875 2.994 7.607 3.347 L 7.731 3.406 C 8.562 3.805 8.89 4.105 8.89 4.755 C 8.89 5.74 8.451 6.43 7.589 6.774 L 7.372 6.292 C 8.019 6.034 8.334 5.539 8.334 4.755 C 8.334 4.369 8.137 4.189 7.479 3.873 L 7.354 3.813 C 6.455 3.38 6.071 2.999 6.071 2.203 Z" />
        <path d="M 10.77 2.203 C 10.77 1.025 11.319 0.267 12.389 0 L 12.531 0.506 C 11.719 0.709 11.327 1.251 11.327 2.203 C 11.327 2.749 11.575 2.994 12.307 3.347 L 12.431 3.406 C 13.262 3.805 13.59 4.105 13.59 4.755 C 13.59 5.74 13.151 6.43 12.289 6.774 L 12.072 6.292 C 12.718 6.034 13.034 5.539 13.034 4.755 C 13.034 4.369 12.837 4.189 12.178 3.873 L 12.054 3.813 C 11.154 3.38 10.77 2.999 10.77 2.203 Z" />
        <path d="M 16.148 0 C 15.078 0.267 14.53 1.025 14.53 2.203 C 14.53 2.999 14.914 3.38 15.813 3.813 L 15.938 3.873 C 16.596 4.189 16.793 4.369 16.793 4.755 C 16.793 5.539 16.478 6.034 15.831 6.292 L 16.048 6.774 C 16.911 6.43 17.35 5.74 17.35 4.755 C 17.35 4.105 17.021 3.805 16.191 3.406 L 16.067 3.347 C 15.334 2.994 15.086 2.749 15.086 2.203 C 15.086 1.251 15.478 0.709 16.291 0.506 L 16.148 0 Z" />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M 21.945 14.407 C 24.077 14.407 25.809 16.111 25.809 18.211 L 25.809 20.872 C 25.809 22.972 24.077 24.678 21.945 24.678 L 21.175 24.678 L 21.175 26.195 C 21.175 28.295 17.375 30 12.688 30 C 7.991 30 4.191 28.295 4.191 26.195 L 4.191 12.514 C 4.191 10.414 7.991 8.71 12.688 8.71 C 17.375 8.71 21.175 10.414 21.175 12.514 L 21.175 14.407 L 21.945 14.407 Z M 12.688 15.55 C 16.952 15.55 20.404 14.188 20.404 12.514 C 20.404 10.83 16.952 9.469 12.688 9.469 C 8.424 9.469 4.962 10.83 4.962 12.514 C 4.962 14.188 8.424 15.55 12.688 15.55 Z M 20.404 14.084 L 20.404 26.195 C 20.404 27.879 16.952 29.241 12.688 29.241 C 8.424 29.241 4.962 27.879 4.962 26.195 L 4.962 14.084 C 6.302 15.394 9.247 16.309 12.688 16.309 C 16.119 16.309 19.063 15.394 20.404 14.084 Z M 25.038 20.872 L 25.038 18.211 C 25.038 16.537 23.655 15.176 21.945 15.176 L 21.175 15.176 L 21.175 23.918 L 21.945 23.918 C 23.655 23.918 25.038 22.557 25.038 20.872 Z"
        />
      </svg>
    );
  };

  const lunchIcon = () => {
    return (
      <svg width="30" height="30" viewBox="0 0 30 30" fill="#000000">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M 4.427 2.774 L 25.631 2.774 L 27.908 9.138 L 28.393 24.611 L 22.569 30 L 2.4 30 L 1.608 10.613 L 4.427 2.774 Z M 4.891 3.434 L 2.272 10.715 L 3.033 29.341 L 22.11 29.341 L 22.11 11.412 L 24.932 3.434 L 4.891 3.434 Z M 25.4 4.088 L 27.252 9.262 L 27.724 24.331 L 22.77 28.916 L 22.77 11.525 L 25.4 4.088 Z"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M 25.398 0.684 C 24.775 0.684 24.294 1.165 24.294 1.729 C 24.294 2.293 24.775 2.774 25.398 2.774 C 26.022 2.774 26.503 2.293 26.503 1.729 C 26.503 1.165 26.022 0.684 25.398 0.684 Z M 23.634 1.729 C 23.634 0.775 24.437 0.024 25.398 0.024 C 26.359 0.024 27.163 0.775 27.163 1.729 C 27.163 2.684 26.359 3.434 25.398 3.434 C 24.437 3.434 23.634 2.684 23.634 1.729 Z"
        />
        <path d="M 4.569 0.66 L 4.58 0.66 L 4.587 0.66 L 4.589 0.66 L 4.599 0.66 L 24.553 0.66 C 24.756 0.454 25.02 0.33 25.308 0.33 C 25.434 0.33 25.555 0.353 25.668 0.397 L 25.668 0 L 4.603 0 L 4.582 0 L 4.569 0 C 3.692 0 3.074 0.823 3.074 1.714 C 3.074 2.605 3.692 3.428 4.569 3.428 L 4.603 3.428 L 25.668 3.428 L 25.668 3.031 C 25.555 3.074 25.434 3.098 25.308 3.098 C 25.02 3.098 24.756 2.974 24.553 2.768 L 4.599 2.768 L 4.569 2.768 C 4.158 2.768 3.734 2.351 3.734 1.714 C 3.734 1.077 4.158 0.66 4.569 0.66 Z" />
        <path d="M 15.922 10.701 C 15.159 10.701 14.542 11.362 14.542 12.179 L 14.542 18.6 C 14.542 18.877 14.743 19.093 15.002 19.093 L 15.922 19.093 L 15.922 24.278 C 15.922 24.42 16.023 24.528 16.156 24.528 L 17.076 24.528 C 17.202 24.528 17.303 24.42 17.303 24.278 L 17.303 12.179 C 17.303 11.362 16.685 10.701 15.922 10.701 Z M 12.46 10.701 C 12.082 10.701 11.773 11.032 11.773 11.443 L 11.773 16.136 L 11.313 16.136 L 11.313 11.443 C 11.313 11.032 10.998 10.701 10.619 10.701 C 10.235 10.701 9.926 11.032 9.926 11.443 L 9.926 16.136 L 9.466 16.136 L 9.466 11.443 C 9.466 11.032 9.156 10.701 8.772 10.701 C 8.394 10.701 8.085 11.032 8.085 11.443 L 8.085 16.629 C 8.085 17.445 8.702 18.107 9.466 18.107 L 9.926 18.107 L 9.926 24.278 C 9.926 24.42 10.033 24.528 10.159 24.528 L 11.079 24.528 C 11.206 24.528 11.313 24.42 11.313 24.278 L 11.313 18.107 L 11.773 18.107 C 12.536 18.107 13.154 17.445 13.154 16.629 L 13.154 11.443 C 13.154 11.032 12.845 10.701 12.46 10.701 Z M 16.843 18.6 L 15.002 18.6 L 15.002 12.179 C 15.002 11.639 15.411 11.194 15.922 11.194 C 16.433 11.194 16.843 11.639 16.843 12.179 L 16.843 18.6 Z M 12.694 16.629 C 12.694 17.176 12.284 17.614 11.773 17.614 L 9.466 17.614 C 8.955 17.614 8.545 17.176 8.545 16.629 L 8.545 11.443 C 8.545 11.308 8.646 11.194 8.772 11.194 C 8.898 11.194 9.005 11.308 9.005 11.443 L 9.005 16.136 C 9.005 16.406 9.213 16.629 9.466 16.629 L 9.926 16.629 C 10.184 16.629 10.386 16.406 10.386 16.136 L 10.386 11.443 C 10.386 11.308 10.494 11.194 10.619 11.194 C 10.746 11.194 10.846 11.308 10.846 11.443 L 10.846 16.136 C 10.846 16.406 11.054 16.629 11.313 16.629 L 11.773 16.629 C 12.025 16.629 12.233 16.406 12.233 16.136 L 12.233 11.443 C 12.233 11.308 12.335 11.194 12.46 11.194 C 12.593 11.194 12.694 11.308 12.694 11.443 L 12.694 16.629 Z M 10.846 24.035 L 10.386 24.035 L 10.386 18.107 L 10.846 18.107 L 10.846 24.035 Z M 16.843 24.035 L 16.383 24.035 L 16.383 19.093 L 16.843 19.093 L 16.843 24.035 Z" />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M 14.459 12.179 C 14.459 11.322 15.109 10.618 15.923 10.618 C 16.737 10.618 17.386 11.322 17.386 12.179 L 17.386 24.278 C 17.386 24.459 17.255 24.611 17.077 24.611 L 16.156 24.611 C 15.973 24.611 15.84 24.46 15.84 24.278 L 15.84 19.175 L 15.002 19.175 C 14.693 19.175 14.459 18.917 14.459 18.6 L 14.459 12.179 Z M 11.692 11.443 C 11.692 10.992 12.031 10.618 12.461 10.618 C 12.897 10.618 13.237 10.991 13.237 11.443 L 13.237 16.629 C 13.237 17.486 12.587 18.189 11.774 18.189 L 11.396 18.189 L 11.396 24.278 C 11.396 24.461 11.256 24.611 11.08 24.611 L 10.16 24.611 C 9.984 24.611 9.844 24.461 9.844 24.278 L 9.844 18.189 L 9.466 18.189 C 8.652 18.189 8.003 17.486 8.003 16.629 L 8.003 11.443 C 8.003 10.992 8.343 10.618 8.773 10.618 C 9.208 10.618 9.549 10.991 9.549 11.443 L 9.549 16.053 L 9.844 16.053 L 9.844 11.443 C 9.844 10.991 10.185 10.618 10.62 10.618 C 11.049 10.618 11.396 10.99 11.396 11.443 L 11.396 16.053 L 11.692 16.053 L 11.692 11.443 Z M 15.085 18.517 L 15.085 12.179 C 15.085 11.679 15.463 11.276 15.923 11.276 C 16.382 11.276 16.761 11.679 16.761 12.179 L 16.761 18.517 L 15.085 18.517 Z M 11.774 17.532 C 12.234 17.532 12.612 17.135 12.612 16.629 L 12.612 11.443 C 12.612 11.347 12.541 11.276 12.461 11.276 C 12.389 11.276 12.316 11.345 12.316 11.443 L 12.316 16.136 C 12.316 16.446 12.077 16.711 11.774 16.711 L 11.314 16.711 C 11.005 16.711 10.765 16.447 10.765 16.136 L 10.765 11.443 C 10.765 11.345 10.693 11.276 10.62 11.276 C 10.545 11.276 10.469 11.348 10.469 11.443 L 10.469 16.136 C 10.469 16.445 10.236 16.711 9.927 16.711 L 9.466 16.711 C 9.163 16.711 8.923 16.446 8.923 16.136 L 8.923 11.443 C 8.923 11.348 8.848 11.276 8.773 11.276 C 8.7 11.276 8.628 11.345 8.628 11.443 L 8.628 16.629 C 8.628 17.135 9.006 17.532 9.466 17.532 L 11.774 17.532 Z M 10.469 23.952 L 10.765 23.952 L 10.765 18.189 L 10.469 18.189 L 10.469 23.952 Z M 16.466 23.952 L 16.761 23.952 L 16.761 19.175 L 16.466 19.175 L 16.466 23.952 Z M 14.542 12.179 C 14.542 11.362 15.16 10.701 15.923 10.701 C 16.685 10.701 17.304 11.362 17.304 12.179 L 17.304 24.278 C 17.304 24.42 17.203 24.528 17.077 24.528 L 16.156 24.528 C 16.024 24.528 15.923 24.42 15.923 24.278 L 15.923 19.092 L 15.002 19.092 C 14.743 19.092 14.542 18.877 14.542 18.6 L 14.542 12.179 Z M 11.774 11.443 C 11.774 11.031 12.083 10.701 12.461 10.701 C 12.846 10.701 13.155 11.031 13.155 11.443 L 13.155 16.629 C 13.155 17.445 12.537 18.107 11.774 18.107 L 11.314 18.107 L 11.314 24.278 C 11.314 24.42 11.206 24.528 11.08 24.528 L 10.16 24.528 C 10.033 24.528 9.927 24.42 9.927 24.278 L 9.927 18.107 L 9.466 18.107 C 8.703 18.107 8.085 17.445 8.085 16.629 L 8.085 11.443 C 8.085 11.031 8.394 10.701 8.773 10.701 C 9.157 10.701 9.466 11.031 9.466 11.443 L 9.466 16.136 L 9.927 16.136 L 9.927 11.443 C 9.927 11.031 10.235 10.701 10.62 10.701 C 10.998 10.701 11.314 11.031 11.314 11.443 L 11.314 16.136 L 11.774 16.136 L 11.774 11.443 Z M 16.843 18.6 L 16.843 12.179 C 16.843 11.639 16.433 11.194 15.923 11.194 C 15.412 11.194 15.002 11.639 15.002 12.179 L 15.002 18.6 L 16.843 18.6 Z M 11.774 17.614 C 12.285 17.614 12.694 17.175 12.694 16.629 L 12.694 11.443 C 12.694 11.308 12.593 11.194 12.461 11.194 C 12.335 11.194 12.234 11.308 12.234 11.443 L 12.234 16.136 C 12.234 16.406 12.026 16.629 11.774 16.629 L 11.314 16.629 C 11.055 16.629 10.847 16.406 10.847 16.136 L 10.847 11.443 C 10.847 11.308 10.746 11.194 10.62 11.194 C 10.494 11.194 10.387 11.308 10.387 11.443 L 10.387 16.136 C 10.387 16.406 10.185 16.629 9.927 16.629 L 9.466 16.629 C 9.214 16.629 9.006 16.406 9.006 16.136 L 9.006 11.443 C 9.006 11.308 8.899 11.194 8.773 11.194 C 8.646 11.194 8.546 11.308 8.546 11.443 L 8.546 16.629 C 8.546 17.175 8.956 17.614 9.466 17.614 L 11.774 17.614 Z M 10.847 24.035 L 10.847 18.107 L 10.387 18.107 L 10.387 24.035 L 10.847 24.035 Z M 16.843 24.035 L 16.843 19.092 L 16.383 19.092 L 16.383 24.035 L 16.843 24.035 Z"
        />
      </svg>
    );
  };

  const dinnerIcon = () => {
    return (
      <svg width="30" height="30" viewBox="0 0 30 30" fill="#000000">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M 16.074 23.162 C 20.582 23.162 24.236 19.508 24.236 15 C 24.236 10.492 20.582 6.837 16.074 6.837 C 11.566 6.837 7.912 10.492 7.912 15 C 7.912 19.508 11.566 23.162 16.074 23.162 Z M 16.074 23.707 C 20.883 23.707 24.781 19.809 24.781 15 C 24.781 10.191 20.883 6.293 16.074 6.293 C 11.265 6.293 7.367 10.191 7.367 15 C 7.367 19.809 11.265 23.707 16.074 23.707 Z"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M 16.074 19.68 C 18.659 19.68 20.754 17.584 20.754 15 C 20.754 12.415 18.659 10.32 16.074 10.32 C 13.489 10.32 11.394 12.415 11.394 15 C 11.394 17.584 13.489 19.68 16.074 19.68 Z M 16.074 20.224 C 18.959 20.224 21.298 17.885 21.298 15 C 21.298 12.115 18.959 9.776 16.074 9.776 C 13.189 9.776 10.85 12.115 10.85 15 C 10.85 17.885 13.189 20.224 16.074 20.224 Z"
        />
        <path d="M 5.087 7.382 C 4.647 7.382 4.288 7.766 4.288 8.245 L 4.288 13.7 L 3.753 13.7 L 3.753 8.245 C 3.753 7.766 3.387 7.382 2.947 7.382 C 2.499 7.382 2.14 7.766 2.14 8.245 L 2.14 13.7 L 1.605 13.7 L 1.605 8.245 C 1.605 7.766 1.246 7.382 0.799 7.382 C 0.359 7.382 0 7.766 0 8.245 L 0 14.273 C 0 15.222 0.718 15.991 1.605 15.991 L 2.14 15.991 L 2.14 23.165 C 2.14 23.33 2.265 23.455 2.412 23.455 L 3.482 23.455 C 3.628 23.455 3.753 23.33 3.753 23.165 L 3.753 15.991 L 4.288 15.991 C 5.175 15.991 5.893 15.222 5.893 14.273 L 5.893 8.245 C 5.893 7.766 5.534 7.382 5.087 7.382 Z M 5.358 14.273 C 5.358 14.908 4.882 15.419 4.288 15.419 L 1.605 15.419 C 1.012 15.419 0.535 14.908 0.535 14.273 L 0.535 8.245 C 0.535 8.088 0.652 7.955 0.799 7.955 C 0.946 7.955 1.07 8.088 1.07 8.245 L 1.07 13.7 C 1.07 14.013 1.312 14.273 1.605 14.273 L 2.14 14.273 C 2.441 14.273 2.675 14.013 2.675 13.7 L 2.675 8.245 C 2.675 8.088 2.8 7.955 2.947 7.955 C 3.093 7.955 3.211 8.088 3.211 8.245 L 3.211 13.7 C 3.211 14.013 3.453 14.273 3.753 14.273 L 4.288 14.273 C 4.581 14.273 4.823 14.013 4.823 13.7 L 4.823 8.245 C 4.823 8.088 4.94 7.955 5.087 7.955 C 5.241 7.955 5.358 8.088 5.358 8.245 L 5.358 14.273 Z M 3.211 22.882 L 2.675 22.882 L 2.675 15.991 L 3.211 15.991 L 3.211 22.882 Z" />
        <path d="M 28.395 7.382 C 27.508 7.382 26.79 8.151 26.79 9.1 L 26.79 16.564 C 26.79 16.886 27.024 17.137 27.325 17.137 L 28.395 17.137 L 28.395 23.165 C 28.395 23.33 28.512 23.455 28.666 23.455 L 29.736 23.455 C 29.883 23.455 30 23.33 30 23.165 L 30 9.1 C 30 8.151 29.282 7.382 28.395 7.382 Z M 29.465 16.564 L 27.325 16.564 L 27.325 9.1 C 27.325 8.473 27.801 7.955 28.395 7.955 C 28.988 7.955 29.465 8.473 29.465 9.1 L 29.465 16.564 Z M 29.465 22.882 L 28.93 22.882 L 28.93 17.137 L 29.465 17.137 L 29.465 22.882 Z" />
      </svg>
    );
  };

  const pieChartData = (meal: AveragePlateMealDataResponse) =>
    (meal && Object.values(meal).some(value => !!value))
      ? [
        { title: t('Vegetable'), value: meal['vegetable'], color: '#36c2b4' },
        { title: t('Grains'), value: meal['grains'], color: '#ffdd00' },
        { title: t('Fruit'), value: meal['fruit'], color: '#f15937' },
        { title: t('Protein'), value: meal['protein'], color: '#ff9224' },
      ]
      : [{ value: 1, color: '#d3d3d3' }];

  const pieChartLabel = (meal: AveragePlateMealDataResponse) =>
    (meal && Object.values(meal).some(value => !!value))
      ? ({ dataEntry }) => `${Math.round(dataEntry.percentage)} %`
      : () => '0 %';

  const mediterraneanScoreQuery = useQuery(
    ['patient-mediterranean-score', patient.patient_id, since, until],
    async () => {
      const res = await metricsApi.appApiPatientReportsSectionsMindGetMediterraneanScore({
        patient_id: patient.patient_id,
        date_since: since.format('YYYY-MM-DD'),
        date_until: until.format('YYYY-MM-DD'),
        disaggregated: true,
        use_ranking_filter: false,
      });
      return res.data;
    },
    { enabled: hasUpdatedDates },
  );

  const medScoreData = mediterraneanScoreQuery.data;
  const medScoreVersion = medScoreData?.version ?? '1';
  const medScoreFields = [
    { label: t('Vegetables'), value: medScoreData?.vegetable_score ?? 0, color: '#36c2b4' },
    { label: t('Fruit'), value: medScoreData?.fruit_score ?? 0, color: '#f15937' },
    { label: t('Legumes'), value: medScoreData?.legume_score ?? 0, color: '#1e9600' },
    { label: t('Whole Grains'), value: medScoreData?.whole_grains_score ?? 0, color: '#ffdd00' },
    { label: t('Nuts & Seeds'), value: medScoreData?.nuts_seeds_score ?? 0, color: '#528ec3' },
    { label: t('Fats'), value: medScoreData?.fat_score ?? 0, color: '#ff9224' },
    { label: t('Fish'), value: medScoreData?.fish_score ?? 0, color: '#7d87f5' },
    { label: t('Red Meat'), value: medScoreData?.red_meat_score ?? 0, color: '#f15a37' },
    ...(medScoreVersion === '2'
      ? [
        { label: t('Sugary Beverages'), value: medScoreData?.sugary_beverage_score ?? 0, color: '#ff4081' },
        {
          label: t('Ultra-processed Desserts'),
          value: medScoreData?.ultraprocessed_desserts_score ?? 0,
          color: '#9c27b0',
        },
      ]
      : []),
  ];
  const medScoreMaxScore = medScoreData?.max_score ?? (medScoreVersion === '2' ? 10 : 8);
  const medScoreLabelSplice = medScoreVersion === '2' ? 5 : 4;

  return (
    <ErrorBoundary
      fallbackRender={() => (
        <div>
          <Trans>Error loading patient summary</Trans>
        </div>
      )}
      onError={(error) => Sentry.captureException(error)}
    >
      <Grid container style={{ marginTop: 30, marginBottom: 30 }} spacing={2}>
        <Grid item md={6}>
          <Grid container spacing={4} style={{ paddingLeft: 15, paddingRight: 15 }}>
            <Grid item xs={12} style={{ marginBottom: -40 }}>
              <label>
                <h3>
                  <Trans>Average Plate Summary</Trans>
                </h3>
                <div className="pieChartLegend">
                  <Trans>Legend</Trans>:
                  <div className="pieChartLegendItem">
                    <Trans>Vegetable</Trans>
                    <CircleDot color="#36c2b4" size="14px" />
                  </div>
                  <div className="pieChartLegendItem">
                    <Trans>Grains</Trans>
                    <CircleDot color="#ffdd00" size="14px" />
                  </div>
                  <div className="pieChartLegendItem">
                    <Trans>Fruit</Trans>
                    <CircleDot color="#f15937" size="14px" />
                  </div>
                  <div className="pieChartLegendItem">
                    <Trans>Protein</Trans>
                    <CircleDot color="#ff9224" size="14px" />
                  </div>
                </div>
                <br />
                <br />
              </label>
            </Grid>
            {averagePlateQuery.isLoading
              ? <LoadingSpinner />
              : averagePlateQuery.isError
              ? (
                <Grid item xs={12} style={{ marginTop: -30, marginBottom: 40 }}>
                  <div className="text-muted">
                    <Trans>No meals found</Trans>
                  </div>
                </Grid>
              )
              : !!averagePlateData && (
                <>
                  <Grid item xs={4}>
                    <div style={{ position: 'relative', minHeight: 50, height: '100%' }}>
                      <PieChart
                        lineWidth={35}
                        label={pieChartLabel(averagePlateData.breakfast)}
                        labelStyle={{
                          fontSize: '6px',
                        }}
                        labelPosition={85}
                        data={pieChartData(averagePlateData.breakfast)}
                        viewBoxSize={[100, 100]}
                      />
                      <div style={iconBoxStyle}>
                        <div style={{ textAlign: 'center' }}>
                          {breakfastIcon()}
                          <br />
                          <Trans>Breakfast</Trans>
                        </div>
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={4}>
                    <div style={{ position: 'relative', minHeight: 50, height: '100%' }}>
                      <PieChart
                        lineWidth={35}
                        label={pieChartLabel(averagePlateData.lunch)}
                        labelStyle={{
                          fontSize: '6px',
                        }}
                        labelPosition={85}
                        data={pieChartData(averagePlateData.lunch)}
                        viewBoxSize={[100, 100]}
                      />
                      <div style={iconBoxStyle}>
                        <div style={{ textAlign: 'center' }}>
                          {lunchIcon()}
                          <br />
                          <Trans>Lunch</Trans>
                        </div>
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={4}>
                    <div style={{ position: 'relative', minHeight: 50, height: '100%' }}>
                      <PieChart
                        lineWidth={35}
                        label={pieChartLabel(averagePlateData.dinner)}
                        labelStyle={{
                          fontSize: '6px',
                        }}
                        labelPosition={85}
                        data={pieChartData(averagePlateData.dinner)}
                        viewBoxSize={[100, 100]}
                      />
                      <div style={iconBoxStyle}>
                        <div style={{ textAlign: 'center' }}>
                          {dinnerIcon()}
                          <br />
                          <Trans>Dinner</Trans>
                        </div>
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <div>
                      {t(
                        'Meals from {{DateSince}} to {{DateUntil}} ({{NumMeals}} meals were logged over {{NumMealDays}} days)',
                        {
                          DateSince: since.format('YYYY-MM-DD'),
                          DateUntil: until.format('YYYY-MM-DD'),
                          NumMeals: averagePlateData.n_meals,
                          NumMealDays: (DateTime.fromISO(averagePlateData.date_until).diff(
                            DateTime.fromISO(averagePlateData.date_since),
                            'days',
                          ).days + 1),
                        },
                      )}
                      <br />
                      <label style={{ fontSize: '10px' }}>
                        <Trans>Charts are not displayed for meals with no data in given date range</Trans>
                      </label>
                    </div>
                  </Grid>
                </>
              )}
          </Grid>
          <Grid container spacing={4} style={{ paddingLeft: 15, paddingRight: 15, marginTop: 20 }}>
            <Grid item xs={12}>
              <h3>
                <Trans>Mediterranean Score</Trans>
              </h3>
              {mediterraneanScoreQuery.isLoading
                ? <LoadingSpinner />
                : (
                  <Grid container spacing={2} style={{ display: 'flex', alignItems: 'center', height: 200 }}>
                    <Grid
                      item
                      xs={4}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%',
                      }}
                    >
                      {medScoreFields.slice(0, medScoreLabelSplice).map((field, index) => (
                        <div key={index}>
                          <div style={{ marginBottom: 4 }}>
                            <div style={{ clear: 'both' }} />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: '50%' }}>{field.label}</span>
                            <div style={{ width: '50%' }}>
                              <MedScoreLinearProgress
                                value={field.value || 0}
                                color={field.color}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </Grid>
                    <Grid item xs={4} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <div style={{ position: 'relative', width: 200, height: 200 }}>
                        <PieChart
                          data={[{ value: medScoreData?.total_score || 0, color: '#ffdc00' }]}
                          totalValue={medScoreMaxScore}
                          lineWidth={30}
                          label={() => `${Math.round((medScoreData?.total_score || 0) * 10) / 10}`}
                          labelStyle={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                          }}
                          labelPosition={0}
                          startAngle={270}
                          background="#f3f3f3"
                        />
                        <div style={{ position: 'absolute', bottom: 55, width: '100%', textAlign: 'center' }}>
                          <div style={{ fontSize: '14px' }}>
                            <Trans>out of {{ medScoreMaxScore }}</Trans>
                          </div>
                        </div>
                      </div>
                    </Grid>
                    <Grid
                      item
                      xs={4}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%',
                      }}
                    >
                      {medScoreFields.slice(medScoreLabelSplice).map((field, index) => (
                        <div key={index}>
                          <div style={{ marginBottom: 4 }}>
                            <div style={{ clear: 'both' }} />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: '50%' }}>{field.label}</span>
                            <div style={{ width: '50%' }}>
                              <MedScoreLinearProgress
                                value={field.value || 0}
                                color={field.color}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </Grid>
                  </Grid>
                )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item md={6} sm={12} style={{ paddingLeft: '15px', paddingRight: '15px' }}>
          {flags?.patient_show_dexcom_oauth && (
            <div>
              <RxErrorBoundary>
                {!!averagePlateData && <AgpDateRange />}
              </RxErrorBoundary>
            </div>
          )}
        </Grid>
      </Grid>
      <Grid item md={12} sm={12} lg={12} style={{ marginTop: '80px' }}>
        <PatientNote />
      </Grid>
      <div style={{ height: 50 }} />
    </ErrorBoundary>
  );
};

const isEmptyQuill = (html: string) =>
  !html.replace(/<(.|\n)*?>/g, '').replace(/&nbsp;/g, ' ').trim();

export const PatientNote = () => {
  const { t } = useTranslation();
  const { patient, clinician } = useStore();
  const queryClient = useQueryClient();
  const [noteInput, setNoteInput] = useState('');

  const notesQuery = useQuery(['patientNotes', patient.patient_id], async () => {
    const res = await patientApi.appApiPatientNoteGetPatientNotes({
      patient_id: patient.patient_id,
    });
    return res.data ?? [];
  });

  const postNoteMutation = useMutation({
    mutationFn: async (newNote: string) => {
      return await patientApi.appApiPatientNotePostPatientNote({
        patient_id: patient.patient_id,
        CreatePatientNoteRequest: { note: newNote },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patientNotes', patient.patient_id] });
      setNoteInput('');
    },
    onError: (err) => {
      console.log(err);
    },
  });
  const saveNote = (e) => {
    e.preventDefault();
    if (isEmptyQuill(noteInput) || postNoteMutation.isLoading) {
      return;
    }
    postNoteMutation.mutate(noteInput);
    track(ANALYTICS_EVENTS.PATIENT_NOTE_SAVED, {
      'Patient ID': patient.patient_id,
      'Note Length': noteInput.replace(/<(.|\n)*?>/g, '').trim().length || 0,
    });
  };

  const notes = notesQuery.data ?? [];

  return (
    <div style={{ width: '700px', margin: '0 auto' }}>
      <div className="mb-3" style={{ fontSize: '18px' }}>
        <Trans>Clinician notes</Trans>
      </div>
      {notesQuery.isError && (
        <div>
          <Trans>Unexpected error loading notes:</Trans>
          {'' + notesQuery.error}
        </div>
      )}
      {notesQuery.isSuccess && notes.length === 0 && (
        <div className="text-muted mb-3">
          <Trans>No notes yet. Add the first note for this patient.</Trans>
        </div>
      )}
      {notes.map((note) => {
        const own = String(note.created_by_user_id) === String(clinician.id);
        const author = own ? t('You') : `${t('Clinician')} ${note.created_by_user_id ?? ''}`;
        return (
          <div
            key={note.id}
            className="mb-3"
            style={{ borderBottom: '1px solid #eee', paddingBottom: 12 }}
          >
            <div className="text-muted mb-1" style={{ fontSize: '14.4px' }}>
              {author}
              {note.created_time
                ? (' ' + DateTime.fromISO(note.created_time).toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY))
                : null}
            </div>
            <div dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(note.note) }} />
          </div>
        );
      })}
      <ReactQuill
        theme="snow"
        className="qEditor"
        value={noteInput}
        onChange={setNoteInput}
      />
      <Button block onClick={saveNote} disabled={postNoteMutation.isLoading}>
        <Trans>Add Note</Trans>
      </Button>
      <div className="mt-3">
        {postNoteMutation.isError
          ? (
            <div>
              <Trans>Unexpected error saving note:</Trans>
              {'' + postNoteMutation.error}
            </div>
          )
          : postNoteMutation.isSuccess
            && (
              <ConfigProvider theme={{ token: antdAlertConfig }}>
                <Alert
                  message={t('Note saved')}
                  type="success"
                  closable
                />
              </ConfigProvider>
            )}
      </div>
    </div>
  );
};

export const antdAlertConfig = {
  fontFamily: 'geomanist',
  fontSize: 16,
  borderRadius: 0,
  colorSuccessBorder: '#36c2b4',
  colorSuccessBg: '#fff',
  colorText: '#6c757d',
};

export default withFilterPanel(Summary);
